import './EditOrder.style.less'

import {
  Button,
  Form,
  FormInstance,
  message,
  notification,
  Popover,
  Row,
  Space,
  Tabs,
} from 'antd'
import { useAtom } from 'jotai'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import OrderDetailForm from './components/OrderDetailForm/OrderDetailForm'
import cx from 'classnames'
import { themeAtom } from '@/jotai/theme.jotai'
import { isDark } from '@/utils/darkmode'
import ProductDetailForm from './components/ProductDetailForm'
import ProductRequirementForm from './components/ProductRequirementForm'
import useOrder, {
  useGetOrderAutoGeneratePropertySetting,
  useGetOrderCompanySetting,
  useGetOrderDetail,
  useGetSequentialOrderProperty,
} from '../Order.hook'
import {
  formatRequirementFormDataToObject,
  IsExternalUser,
  toFormObjectValues,
} from './EditOrder.helper'
import { COLLABORATOR_ROLE_OWNER } from '../Order.constant'
import BackHeader from '@/layout/components/BackHeader'
import { getUrlParams, isEmptyValue } from '@/utils/common'
import { useMutation, useQuery } from 'react-query'
import {
  createOrderTemplate,
  getOrderItemSetting,
  updateOrderTemplate,
  updateCollaborator,
} from '../Order.transform'
import LoadingComponent from '@/components/LoadingComponent/LoadingComponent'
import ModalConfirm from '@/components/ModalConfirm/ModalConfirm'
import useMasterData from '@/hook/MasterData'
import ModalSaveOrderTemplate from './components/ModalOrderSaveTemplate/ModalSaveOrderTemplate'
import { CloseCircleFilled, DownOutlined } from '@ant-design/icons'
import LoadOrderTemplate from './components/LoadOrderTemplate/LoadOrderTemplate'
import {
  cloneDeep,
  find,
  get,
  includes,
  keyBy,
  keys,
  lowerCase,
  map,
  orderBy,
  size,
  uniqBy,
} from 'lodash'
import { isFieldChangeAtom } from '@/jotai/order.jotai'
import { orderCreationTabNumberAtom } from '../../jotai/order.jotai'
import { IInputProperty } from '../../interface/common.interface'
import { useAuth } from '@/providers/auth.provider'
import { getValueDynamic } from '../Landing/components/OrderTable/OrderTable.transform'
import { queryClient } from '@/config/react-query'
import { PBF_QUERY_KEY } from '@/constant/pbf'
import { IsAdminRole } from '@/utils/users'

const { GET_ORDER_SEQUENTIAL_PROPERTY } = PBF_QUERY_KEY
const { TabPane } = Tabs

interface Props {
  hideProperties?: any
}

function EditOrder(props: Props) {
  const navigate = useNavigate()
  const params = useParams()
  const paramId = params?.id
  const typeKey = getUrlParams('type')
  const orderId = getUrlParams('id')
  // Editing an existing order; clone reuses this screen with ?id=<source>.
  const isEditing = !!paramId && typeKey !== 'clone'
  const {
    ///stucture for new order///
    data: orderData,
    modifyCreateOrder,
    isLoadingCreateOrder,
    isSuccessCreated,
    dataupdated,
    dataCreated,
    isSuccessUpdated,
    isLoadingUpdateOrder,
  } = useOrder()

  const { user } = useAuth()

  const { data: masterData } = useMasterData()

  const [modalTemplate, setmodalTemplate] = useState(false)
  const [stateOrderMode, setStateOrderMode] = useState<
    'create' | 'clone' | 'edit'
  >('create')
  const [modalLoadTemplate, setModalLoadTemplate] = useState(false)
  const [modalConfirmCancel, setmodalConfirmCancel] = useState(false)
  const [saveButtonType, setSaveButtonType] = useState(1)

  const [activeTab, setactiveTab] = useAtom(orderCreationTabNumberAtom)

  const [isFieldChange, setFieldChange] = useAtom(isFieldChangeAtom)

  useEffect(() => {
    const activeTab = getUrlParams('tab')
    if (activeTab) {
      setactiveTab(activeTab)
    }
    return () => {
      setFieldChange(false)
    }
  }, [])

  const { data: orderDetail, isFetching: isFetchingDetail } = useGetOrderDetail(
    { id: typeKey === 'clone' ? orderId : paramId }
  )

  const { data: orderSetting, isFetched: isOrderSettingFetched } =
    useGetOrderCompanySetting({ companyId: orderDetail?.company?.id })
  const { default_fields = [] } = orderSetting ?? {}
  const defaultFieldValue = orderBy(default_fields, ['display_order'], ['asc'])

  const { data: orderItemSetting } = useQuery(
    ['GET_ORDER_ITEM_SETTING', orderDetail?.company?.id],
    async () => {
      return await getOrderItemSetting(orderDetail?.company?.id ?? '')
    }
  )

  const { data: settingAutoGenerateData, isFetched: isAutoGenerateFetched } =
    useGetOrderAutoGeneratePropertySetting()

  const {
    data: dataSequentialPropertyData,
    isRefetching: isRefetchDataSequential,
  } = useGetSequentialOrderProperty(
    { ...settingAutoGenerateData, companyId: user?.company?.id },
    [isAutoGenerateFetched]
  )

  const [attributeSizes, setAttributeSizes] = useState([])
  const [theme] = useAtom(themeAtom)
  const [stateFields, setStateFields] = useState<Array<IInputProperty>>([])

  const [orderForm] = Form.useForm()
  const [productForm] = Form.useForm()
  const [requirementForm] = Form.useForm()
  const [messageApi, contextHolder] = message.useMessage()

  const handleSetOrderDetailAutoField = () => {
    if (!settingAutoGenerateData) return
    const keyDefaultValue: any = keyBy(defaultFieldValue, 'field')
    const [, fieldSequential] = settingAutoGenerateData?.items || []
    const [, existFieldSequential] = map(
      settingAutoGenerateData?.items,
      (item) => getValueDynamic(orderDetail, item.data_source)
    )
    if (!paramId && !typeKey) {
      // CREATE NEW ORDER
      let preDefaultFields = map(defaultFieldValue, (item: any, index) => ({
        ...item,
        display_order: item.display_order || index, // Prevent if the display_order doesn't has.
      }))
      const placeHolderField = get(
        settingAutoGenerateData,
        'place_holder_field',
        {}
      )

      if (size(placeHolderField)) {
        preDefaultFields = map(preDefaultFields, (item) => {
          if (item.field === placeHolderField?.field) {
            return {
              ...item,
              prefix: placeHolderField?.prefix,
              value: placeHolderField?.prefix,
            }
          }
          return item
        })
        // TODO: Special Item
        preDefaultFields.push({
          name: '',
          field: 'sequential_number',
          required: false,
          hidden: true,
          data_type: 'text',
          value: '',
        })
      }

      setStateOrderMode('create')
      setStateFields(preDefaultFields)
    }

    let getFormValue = orderBy(
      orderDetail?.property_value?.values,
      (item) =>
        item.display_order ??
        keyDefaultValue[item.field]?.display_order ?? ['asc']
    )

    getFormValue = map(getFormValue, (item, index) => ({
      ...item,
      display_order: item.display_order || index,
    }))

    if (orderId && orderDetail) {
      // CLONE NEW ORDER
      if (existFieldSequential) {
        getFormValue.push({
          name: '',
          field: fieldSequential.field,
          required: false,
          hidden: true,
          data_type: 'text',
          value: '',
        })
      }

      setStateOrderMode('clone')
      setStateFields(getFormValue)
    } else if (orderDetail) {
      // EDIT ORDER
      if (existFieldSequential) {
        getFormValue.push({
          name: '',
          field: fieldSequential.field,
          required: false,
          hidden: true,
          data_type: 'text',
          value: existFieldSequential.value_format,
        })
      }
      setStateOrderMode('edit')
      setStateFields(getFormValue)
    }
  }

  const displayErrorNotification = () => {
    notification.error({
      message: 'Please input all required fields',
      duration: 3,
      placement: 'top',
    })
  }

  const onOpenModalTemplate = () => {
    setmodalTemplate(true)
  }

  useEffect(() => {
    handleSetOrderDetailAutoField()
  }, [isOrderSettingFetched, isAutoGenerateFetched, orderDetail])

  const nextActiveTab = () => {
    return activeTab == '1' ? '2' : activeTab == '2' ? '3' : '3'
  }

  useEffect(() => {
    if (isSuccessUpdated && dataupdated?.data?.statusCode == 200) {
      messageApi.open({
        type: 'success',
        content: 'Order has been updated.',
      })
      if (saveButtonType == 1) {
        navigate(`/pbf/orders/edit-order/${orderDetail?.id}`, { replace: true })
        setactiveTab(nextActiveTab())
      } else if (saveButtonType == 2) {
        navigate(
          `/pbf/orders/${encodeURIComponent(orderDetail?.number)}?orderKey=${
            orderDetail?.id
          }&activeTab=1`,
          { replace: true }
        )
      } else {
        navigate('/pbf')
      }
      queryClient.refetchQueries([GET_ORDER_SEQUENTIAL_PROPERTY])
      queryClient.refetchQueries(['getOrderDetail'])
    } else if (isSuccessUpdated && dataupdated?.data?.statusCode != 201) {
      if (dataupdated?.data?.statusCode === 409) {
        queryClient.refetchQueries([GET_ORDER_SEQUENTIAL_PROPERTY])
      }
      notification.error({
        message: dataupdated?.data?.message,
        duration: 8,
        placement: 'top',
      })
    }
  }, [isSuccessUpdated])

  useEffect(() => {
    if (isSuccessCreated && dataCreated?.data?.statusCode == 201) {
      orderForm.resetFields()
      productForm.resetFields()
      requirementForm.resetFields()
      messageApi.open({
        type: 'success',
        content: 'Order has been created.',
      })
      if (saveButtonType == 1) {
        navigate('/loading')
        setTimeout(() => {
          navigate(`/pbf/orders/edit-order/${dataCreated?.data?.data?.id}`, {
            replace: true,
          })
          setactiveTab(nextActiveTab())
        }, 300)
      } else if (saveButtonType == 2) {
        navigate(
          `/pbf/orders/${encodeURIComponent(
            dataCreated?.data?.data?.number
          )}?orderKey=${dataCreated?.data?.data?.id}&activeTab=1`,
          { replace: true }
        )
      } else {
        navigate('/pbf')
      }
    } else if (isSuccessCreated && dataCreated?.data?.statusCode != 201) {
      if (dataCreated?.data?.statusCode === 409) {
        queryClient.refetchQueries([GET_ORDER_SEQUENTIAL_PROPERTY])
      }
      notification.error({
        message: dataCreated?.data?.message,
        duration: 8,
        placement: 'top',
      })
    }
  }, [isSuccessCreated])

  const saveTemplateMutation = useMutation(async (data) => {
    await createOrderTemplate(data)
  })

  const updateTemplateMutation = useMutation(async ({ id, data }: any) => {
    await updateOrderTemplate(id, data)
  })

  const onSaveTemplate = async (value) => {
    const template = !isEmptyValue(value.templates)
      ? JSON.parse(value.templates)
      : ''
    const payload: any = {
      name: !isEmptyValue(template) ? template?.name : value.name,
      template_value: {
        orders: {
          property_value: {
            values: stateFields?.map((item) => {
              return {
                name: item.name,
                required: item.required,
                field: item.field,
                data_type: item.data_type,
                display_order: item?.display_order,
                value: item.data_type == 'file' ? [] : '',
              }
            }),
          },
        },
      },
      type: 2,
    }
    if (!isEmptyValue(template)) {
      await updateTemplateMutation.mutateAsync({
        id: template.id,
        data: payload,
      })
    } else {
      await saveTemplateMutation.mutateAsync(payload)
    }
    setmodalTemplate(false)
    notification.success({
      message: 'Template Saved',
      duration: 4,
      placement: 'top',
    })
  }

  useEffect(() => {
    if (updateTemplateMutation.isError || saveTemplateMutation.isError) {
      const message = (saveTemplateMutation.error as any)?.response?.data
        ?.message
      notification.error({
        message: message,
        duration: 3,
        placement: 'top',
      })
    }
  }, [updateTemplateMutation.isError, saveTemplateMutation.isError])

  const saveOrderMutation = useMutation(async (payload: any) => {
    if (isEditing && IsAdminRole(user) && !IsExternalUser(orderDetail, user)) {
      const isCollaborator = orderDetail?.assignments?.some(
        (item: any) => item?.user?.id === user?.id
      )
      if (!isCollaborator) {
        try {
          await updateCollaborator(paramId, [
            { ...user, role: COLLABORATOR_ROLE_OWNER, assignmentId: null },
          ])
        } catch (err: any) {
          console.warn('Auto-add admin collaborator notice:', err)
        }
      }
    }
    await modifyCreateOrder(payload)
  })

  const checkFormHasAutoGenerateProps = (
    formData: FormInstance,
    comSetting
  ) => {
    const { action_field = {}, place_holder_field = {} } = comSetting
    const getFormKeys = formData.getFieldsValue()
    const keysList = keys(getFormKeys)
    const resPayload: any = {}

    if (
      includes(keysList, action_field?.field) &&
      includes(keysList, place_holder_field?.field)
    ) {
      resPayload['sequential_number'] = getFormKeys['sequential_number'] // TODO: Move to constant
    }

    return resPayload
  }

  const saveOrder = async () => {
    try {
      await productForm.validateFields()
      await requirementForm.validateFields()
      await orderForm.validateFields()
      const productValue = productForm.getFieldsValue()
      const requirementValue = requirementForm.getFieldsValue()
      const orderDetailForm = orderForm.getFieldsValue()
      const propertyValue = toFormObjectValues(orderDetailForm, stateFields)

      const aqlDataSource =
        find(masterData, (item) => lowerCase(item.name) === 'aql') ?? {}

      const getSequentialNumber = checkFormHasAutoGenerateProps(
        orderForm,
        settingAutoGenerateData
      )
      // extraDataPayload contains lookups original data from database (AQL, ...)
      const extraDataPayload = {
        aqlDataSource,
        autoGenerateData: { ...getSequentialNumber },
      }

      ///Prevent empty POM and duplicate POM///
      const tmp = formatRequirementFormDataToObject(
        requirementValue,
        orderData.orders.requirements
      )

      const measurementValue = tmp.measurement.find(
        (item) => item.field == 'measurementSpec'
      )

      const validation = measurementValue.value.some((element) => {
        delete element.code
        return Object.values(element).some((val) => isEmptyValue(val))
      })

      if (validation) {
        notification.error({
          message:
            'There are empty fields in POM item, Please fill in those values or remove the entire row',
          duration: 3,
          placement: 'top',
          icon: <CloseCircleFilled style={{ color: '#c03261' }} />,
        })
        return
      }

      const lookup = measurementValue.value.reduce((a, e) => {
        a[e.pomDecription] = ++a[e.pomDecription] || 0
        return a
      }, {})

      const duplicates = measurementValue.value.filter(
        (e) => lookup[e.pomDecription]
      )

      if (!isEmptyValue(duplicates)) {
        notification.error({
          message: (
            <div>
              Duplicate POM Description
              <ul>
                {uniqBy(duplicates, (e: any) => {
                  return e.pomDecription
                }).map((item, index) => (
                  <li key={index}>{item.pomDecription}</li>
                ))}
              </ul>
            </div>
          ),
          duration: 3,
          placement: 'top',
          icon: <CloseCircleFilled style={{ color: '#c03261' }} />,
        })
        return
      }
      ///////////////////
      const payload = {
        productValue,
        orderData,
        orderDetail,
        requirementValue,
        orderDetailForm,
        propertyValue,
        type: typeKey,
        orderId: paramId,
        extraDataPayload,
      }

      await saveOrderMutation.mutateAsync(payload)
    } catch (error: any) {
      if (error?.errorFields) {
        displayErrorNotification()
      } else {
        notification.error({
          message:
            error?.response?.data?.message ||
            error?.message ||
            'Failed to save order',
          duration: 4,
          placement: 'top',
        })
      }
    }
  }

  const onApplyTemplates = (value) => {
    const fields = value?.template_value.orders.property_value.values.map(
      (item) => {
        return {
          ...item,
          value: item.data_type == 'file' ? [] : item.value,
        }
      }
    )
    setStateFields(fields)
    setModalLoadTemplate(false)
  }

  const onAddOnProperties = (newFields) => {
    setStateFields(newFields)
  }

  const location = useLocation()
  const onGoBack = () => {
    if (location.key !== 'default') {
      navigate(-1)
    } else {
      navigate('/pbf')
    }
  }

  if (isFetchingDetail) return <LoadingComponent />

  return (
    <>
      <BackHeader
        onClick={() => {
          if (isFieldChange) {
            setmodalConfirmCancel(true)
          } else {
            onGoBack()
          }
        }}
        title={'Back'}
      />
      <div
        className={cx(
          isDark(theme) && 'dark-pbf-order',
          'pbf-edit-order-container'
        )}
      >
        {contextHolder}
        <p className="order-title-text">
          {params.id ? 'Edit Order' : 'Create Order'}
        </p>
        <Tabs
          defaultActiveKey="1"
          activeKey={activeTab as string}
          onChange={(key) => setactiveTab(key)}
        >
          <TabPane tab="Order Info" key="1" forceRender>
            <OrderDetailForm
              onImport={() => {}}
              orderData={orderDetail}
              fields={cloneDeep(stateFields)}
              form={orderForm}
              mode={stateOrderMode}
              addOnProperties={onAddOnProperties}
              isImport={false}
              orderSetting={orderSetting}
              hideProperties={props.hideProperties}
              extraSource={{
                sequentialItems: dataSequentialPropertyData,
                sequentialItemsRefetch: isRefetchDataSequential,
                settingAutoGenerateData,
              }}
            />
          </TabPane>
          <TabPane tab="Product Info" key="2" forceRender>
            <ProductDetailForm
              onImport={() => {}}
              isSuccessCreated={isSuccessCreated}
              fields={stateFields}
              defaultField={
                orderDetail?.product_details ||
                orderData?.orders?.product_details
              }
              form={productForm}
              setField={setStateFields}
              onApplyAttributeSizes={setAttributeSizes}
              orderSetting={orderSetting}
              orderItemSetting={orderItemSetting}
            />
          </TabPane>
          <TabPane tab="Requirements" key="3" forceRender>
            <ProductRequirementForm
              fields={stateFields}
              data={
                !isEmptyValue(orderDetail)
                  ? { ...orderDetail?.requirements, attributes: attributeSizes }
                  : {
                      ...orderData?.orders?.requirements,
                      attributes: attributeSizes,
                    }
              }
              form={requirementForm}
              setField={setStateFields}
              orderSetting={orderSetting}
            />
          </TabPane>
        </Tabs>
        <Row justify="space-between" style={{ marginTop: 15 }}>
          <Popover
            trigger={'click'}
            content={
              <div className="template-button-containers-popover">
                <Button
                  disabled={!isEmptyValue(orderDetail)}
                  size="large"
                  onClick={() => setModalLoadTemplate(true)}
                >
                  Load Template
                </Button>
                <Button size="large" onClick={onOpenModalTemplate}>
                  Save as New Template
                </Button>
              </div>
            }
          >
            <Button
              disabled={isLoadingCreateOrder || isLoadingUpdateOrder}
              className={cx(
                isLoadingCreateOrder || isLoadingUpdateOrder
                  ? ''
                  : 'template-order-button'
              )}
              size="large"
            >
              Template <DownOutlined />
            </Button>
          </Popover>
          <Space size={'large'}>
            <Button
              disabled={
                isLoadingCreateOrder ||
                isLoadingUpdateOrder ||
                saveOrderMutation.isLoading
              }
              className="cane-order-button"
              size="large"
              onClick={() => {
                if (isFieldChange) {
                  setmodalConfirmCancel(true)
                } else {
                  navigate(-1)
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                setSaveButtonType(2)
                await saveOrder()
              }}
              className="save-order-template-button"
              size="large"
              disabled={
                isLoadingCreateOrder ||
                isLoadingUpdateOrder ||
                saveOrderMutation.isLoading
              }
              loading={
                (saveOrderMutation.isLoading && saveButtonType == 2) ||
                (isLoadingCreateOrder && saveButtonType == 2) ||
                (isLoadingUpdateOrder && saveButtonType == 2)
              }
            >
              Save and Close
            </Button>
            <Button
              type="primary"
              size="large"
              className="button-save-order"
              onClick={async () => {
                setSaveButtonType(1)
                await saveOrder()
              }}
              disabled={
                isLoadingCreateOrder ||
                isLoadingUpdateOrder ||
                saveOrderMutation.isLoading
              }
              loading={
                (saveOrderMutation.isLoading && saveButtonType == 1) ||
                (isLoadingCreateOrder && saveButtonType == 1) ||
                (isLoadingUpdateOrder && saveButtonType == 1)
              }
            >
              Save and Continue
            </Button>
          </Space>
        </Row>
      </div>

      {modalConfirmCancel && isFieldChange && (
        <ModalConfirm
          open={modalConfirmCancel}
          onCancel={() => navigate(-1)}
          cancelButtonText="Discard and Leave"
          confirmButtonText="Save and Leave"
          title="You have unsaved changes"
          content={
            <div>
              Do you want to save before leaving? If you leave without saving,
              these changes will be lost.
            </div>
          }
          onClose={() => setmodalConfirmCancel(false)}
          onOK={async () => {
            setmodalConfirmCancel(false)
            setSaveButtonType(3)
            await saveOrder()
          }}
        />
      )}

      {modalTemplate && (
        <ModalSaveOrderTemplate
          open={modalTemplate}
          onClose={() => setmodalTemplate(false)}
          onSave={onSaveTemplate}
        />
      )}

      {modalLoadTemplate && (
        <LoadOrderTemplate
          visible={modalLoadTemplate}
          onClose={() => setModalLoadTemplate(false)}
          onApply={onApplyTemplates}
        />
      )}
    </>
  )
}

export default EditOrder
